import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare, Filter, Loader2, CheckCircle, Clock, Archive, Bug, Lightbulb, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackCenter = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, [categoryFilter, statusFilter]);

  const fetchFeedback = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      let url = `${API}/admin/phase3/feedback?limit=200`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (feedbackId, newStatus, adminNotes = '') => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/phase3/feedback/${feedbackId}`,
        { status: newStatus, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feedback marked as ${newStatus}`);
      fetchFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      console.error('Failed to update feedback:', error);
      toast.error('Failed to update feedback status');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      bug: <Bug className="w-4 h-4" />,
      feature_request: <Lightbulb className="w-4 h-4" />,
      content: <FileText className="w-4 h-4" />,
      general: <MessageSquare className="w-4 h-4" />
    };
    return icons[category] || <MessageSquare className="w-4 h-4" />;
  };

  const getCategoryBadge = (category) => {
    const badges = {
      bug: 'bg-red-100 text-red-800',
      feature_request: 'bg-purple-100 text-purple-800',
      content: 'bg-blue-100 text-blue-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return badges[category] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'bg-blue-100 text-blue-800',
      under_review: 'bg-amber-100 text-amber-800',
      resolved: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-600'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const stats = {
    total: feedback.length,
    new: feedback.filter(f => f.status === 'new').length,
    under_review: feedback.filter(f => f.status === 'under_review').length,
    resolved: feedback.filter(f => f.status === 'resolved').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          💬 Feedback Center
        </h1>
        <p className="text-gray-600">
          Review and respond to user feedback and feature requests
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Total Feedback</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">New</p>
          <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Under Review</p>
          <p className="text-2xl font-bold text-amber-600">{stats.under_review}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Resolved</p>
          <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="under_review">Under Review</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
          >
            <option value="all">All Categories</option>
            <option value="bug">Bug Reports</option>
            <option value="feature_request">Feature Requests</option>
            <option value="content">Content Feedback</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {feedback.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedFeedback(item)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getCategoryBadge(item.category)}`}>
                    {getCategoryIcon(item.category)}
                    {item.category.replace('_', ' ')}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.subject}
                </h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {item.message}
                </p>

                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>👤 {item.username}</span>
                  <span>📅 {new Date(item.created_at).toLocaleDateString()}</span>
                  {item.resolved_at && (
                    <span className="text-green-600">
                      ✅ Resolved {new Date(item.resolved_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {item.admin_notes && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-medium text-amber-900 mb-1">Admin Notes:</p>
                    <p className="text-xs text-amber-800">{item.admin_notes}</p>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex flex-col gap-2 ml-4">
                {item.status === 'new' && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateStatus(item.id, 'under_review');
                    }}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Review
                  </Button>
                )}
                {item.status !== 'resolved' && (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateStatus(item.id, 'resolved');
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Resolve
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        ))}

        {feedback.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No feedback found</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedFeedback(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedFeedback.subject}
            </h2>

            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadge(selectedFeedback.category)}`}>
                {selectedFeedback.category.replace('_', ' ')}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedFeedback.status)}`}>
                {selectedFeedback.status.replace('_', ' ')}
              </span>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>From:</strong> {selectedFeedback.username} ({selectedFeedback.user_id})
              </p>
              <p className="text-sm text-gray-600">
                <strong>Date:</strong> {new Date(selectedFeedback.created_at).toLocaleString()}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Message:</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {selectedFeedback.message}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => handleUpdateStatus(selectedFeedback.id, 'under_review')}
                disabled={selectedFeedback.status === 'under_review'}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
              >
                <Clock className="w-4 h-4 mr-2" />
                Mark Under Review
              </Button>
              <Button
                onClick={() => handleUpdateStatus(selectedFeedback.id, 'resolved')}
                disabled={selectedFeedback.status === 'resolved'}
                className="flex-1 bg-green-500 hover:bg-green-600"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Resolved
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default FeedbackCenter;
